import { useCallback, useEffect, useRef, useState } from 'react';
import * as PIXI from 'pixi.js';
import { sound } from '@pixi/sound';
import { useCanvas } from './useCanvas';
import { useTransform } from './useTransform';
import { useSpriteCreators } from './useSpriteCreators';
import { useAgComponentUpdates } from './useAgComponentUpdates';
import { TransformBoundingBox } from '../components/UI/TransformControls';
import { setGlobalCreateVideoSprite, setGlobalCreateAudioSprite, setGlobalCreateImageSprite, setGlobalCreateGifSprite, setGlobalCreateAiSprite } from '../stores/globalStore';
import { ensureFontLoaded } from '../utils/ensureFontLoaded';
import { calculateBoundingBox } from '../utils/transformUtils.js';
import useCanvasStore from '../stores/canvasStore';
import { sanitizeCaptionStyle, shouldUseWordContainer } from '../utils/captionStyleUtils';

export const useCanvasElements = (viewport, { liteMode = false } = {}) => {
  const {
    addElement,
    updateElement,
    selectElement,
    selectedElement,
    canvasElements,
    pixiApp,
    isPlaying,
    clearAddItemRequest,
    addItemRequest,
    videoTextureManager,
    canvasSize,
    setCanvasSize,
    stopPlaybackLoop,
    setIsElementReloadingFn,
  } = useCanvas();

  // State for canvas size mismatch modal
  const [showSizeMismatchModal, setShowSizeMismatchModal] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const [pendingWorldPosition, setPendingWorldPosition] = useState(null);

  // State for template loading progress
  const [templateLoadingProgress, setTemplateLoadingProgress] = useState({
    isOpen: false,
    progress: 0,
    currentAsset: '',
    totalAssets: 0,
    loadedAssets: 0,
    stage: 'initializing'
  });

  // State for sprite loading indicators (toast notifications)
  const [spriteLoadingItems, setSpriteLoadingItems] = useState([]);
  // Use the transform hook
  const { showTransformControls, hideTransformControls, updateCurrentTransform } = useTransform(viewport, updateElement);

  // Use the AG Component updates hook for toast notifications
  const { handleAgComponentUpdate, updateVisualState, cleanupAll, componentUpdateItems, isElementReloading } = useAgComponentUpdates(stopPlaybackLoop);

  // Register the isElementReloading function with the canvas store
  useEffect(() => {
    if (isElementReloading) {
      setIsElementReloadingFn(isElementReloading);
    }
    return () => {
      setIsElementReloadingFn(null);
    };
  }, [isElementReloading, setIsElementReloadingFn]);

  // Hover state management
  const [hoveredElement, setHoveredElement] = useState(null);
  const hoverBoundingBoxRef = useRef(null);

  // Use the sprite creators hook
  const {
    createVideoSprite,
    createImageSprite,
    createAudioSprite,
    createTextSprite,
    createGifSprite,
    createAiSprite,
  } = useSpriteCreators(
    viewport,
    updateElement,
    selectElement,
    updateCurrentTransform,
    canvasElements,
    setHoveredElement,
    videoTextureManager
  );

  // Setup viewport click handler to deselect elements when clicking empty space
  // Skip in liteMode — interactivity is disabled on the viewport anyway
  if (!liteMode && viewport && !viewport._clickHandlerSetup) {
    viewport.eventMode = "static";
    viewport.on("pointerdown", (event) => {
      // Check if we clicked on empty space (the viewport itself)
      if (event.target === viewport) {
        selectElement(null); // Deselect current element
      }
    });
    viewport._clickHandlerSetup = true;
  }

  // Create hover bounding box (skip in liteMode)
  useEffect(() => {
    if (!viewport || liteMode) return;

    // Create a separate bounding box for hover effect
    const hoverBoundingBox = new TransformBoundingBox(viewport);
    hoverBoundingBoxRef.current = hoverBoundingBox;

    // Add to viewport's interactive container
    if (viewport.interactiveContainer) {
      viewport.interactiveContainer.addChild(hoverBoundingBox.getGraphics());
    }

    return () => {
      if (hoverBoundingBoxRef.current) {
        hoverBoundingBoxRef.current.getGraphics().destroy();
        hoverBoundingBoxRef.current = null;
      }
    };
  }, [viewport, liteMode]);

  // Clear hover state when canvas elements change (e.g., during undo/redo)
  useEffect(() => {
    if (liteMode) return;
    // Check if the currently hovered element still exists
    if (hoveredElement) {
      const elementStillExists = canvasElements.some(
        (el) => el.id === hoveredElement
      );
      if (!elementStillExists) {
        setHoveredElement(null);
      }
    }
  }, [canvasElements, hoveredElement, liteMode]);

  // Update hover bounding box when hoveredElement changes (skip in liteMode)
  useEffect(() => {
    if (liteMode) return;
    if (!hoverBoundingBoxRef.current) return;

    if (hoveredElement && hoveredElement !== selectedElement?.id) {
      // Find the canvas element
      const canvasElement = canvasElements.find(
        (el) => el.id === hoveredElement
      );
      if (canvasElement && canvasElement.pixiSprite && !canvasElement.pixiSprite.destroyed && canvasElement.pixiSprite.scale !== null) {
        // Update with hover styling - light green color with transparency
        hoverBoundingBoxRef.current.update(canvasElement.pixiSprite, {
          color: 0xc6e982, // Light green color matching the handles
          alpha: 0.6,
        });
      } else {
        hoverBoundingBoxRef.current.clear();
      }
    } else {
      // Clear the hover bounding box
      hoverBoundingBoxRef.current.clear();
    }
  }, [hoveredElement, selectedElement, canvasElements, liteMode]);

  /*USE EFFFECT TO TRACK
  JOB STATUS AND UPDATE TOAST
  NOTIFICATIONS */

  // Track which elements have active jobs to avoid unnecessary processing
  const activeJobsRef = useRef(new Set());

  // Monitor canvas elements for job updates and update toast notifications accordingly
  useEffect(() => {
    console.log(activeJobsRef.current, 'active jobs');

    // Early exit if we're playing - no need to update job visuals during playback
    if (isPlaying) {
      cleanupAll();
      return
    };

    // Check if there are any active jobs at all
    const hasActiveJobs = canvasElements.some(element =>
      element.componentData?.id &&
      element.jobStatus &&
      element.jobStatus !== 'finished' &&
      element.jobStatus !== 'error' &&
      element.jobStatus !== ''
    );

    // If no active jobs and we're not tracking any, skip processing entirely
    if (!hasActiveJobs && activeJobsRef.current.size === 0) {
      cleanupAll();
      return;
    }

    // Process all canvas elements to update toast notifications based on job status
    canvasElements.forEach(element => {
      // Only process AG components (those with componentData.id)
      if (!element.componentData?.id) return;

      // Check if element has an active job (including "waiting" status)
      const hasActiveJob = element.jobStatus &&
        element.jobStatus !== 'finished' &&
        element.jobStatus !== 'error' &&
        element.jobStatus !== '';

      if (hasActiveJob) {
        // Track this element as having an active job
        if (!activeJobsRef.current.has(element.id)) {
          activeJobsRef.current.add(element.id);
        }

        // Update toast notification using the hook
        updateVisualState(
          element,
          element.jobStatus,
          element.jobProgress
        );
      } else if (activeJobsRef.current.has(element.id)) {
        // Job just finished or errored - do final update and cleanup
        if (element.jobStatus === 'finished' || element.jobStatus === 'error') {
          // Final toast notification update
          updateVisualState(
            element,
            element.jobStatus,
            element.jobProgress
          );
        }

        // Remove from tracking
        activeJobsRef.current.delete(element.id);
      }
    });
  }, [canvasElements, updateVisualState, isPlaying, cleanupAll]);

  // Show/hide transform controls based on selected element or we are playing the timeline
  // In liteMode, always keep transform controls hidden
  useEffect(() => {
    if (liteMode || !selectedElement || isPlaying) {
      hideTransformControls();
    } else {
      // Find the PIXI sprite for the selected element
      const canvasElement = canvasElements.find(
        (el) => el.id === selectedElement.id
      );
      if (
        canvasElement &&
        canvasElement.pixiSprite &&
        !canvasElement.pixiSprite.destroyed &&
        canvasElement.pixiSprite.scale !== null &&
        canvasElement.pixiSprite.interactiveArea != null
      ) {
        showTransformControls(canvasElement.pixiSprite);
      } else {
        hideTransformControls();
      }
    }
  }, [selectedElement, canvasElements, hideTransformControls, showTransformControls, isPlaying, liteMode]);
  // console.log(canvasElements)

  // Register sprite creators globally so they can be used by undo/redo
  useEffect(() => {
    setGlobalCreateVideoSprite(createVideoSprite);
    setGlobalCreateAudioSprite(createAudioSprite);
    setGlobalCreateImageSprite(createImageSprite);
    setGlobalCreateGifSprite(createGifSprite);
    setGlobalCreateAiSprite(createAiSprite);
  }, [createVideoSprite, createImageSprite, createAudioSprite, createGifSprite, createAiSprite]);

  // Helper function to add a loading item
  const addLoadingItem = useCallback((id, name, type) => {
    setSpriteLoadingItems(prev => [
      ...prev,
      { id, name, type, status: 'loading' }
    ]);
  }, []);

  // Helper function to update loading item status
  const updateLoadingItem = useCallback((id, status, errorMessage = null) => {
    setSpriteLoadingItems(prev =>
      prev.map(item =>
        item.id === id
          ? { ...item, status, errorMessage }
          : item
      )
    );
  }, []);

  // Helper function to remove loading item after delay
  const removeLoadingItem = useCallback((id, delay = 2000) => {
    setTimeout(() => {
      setSpriteLoadingItems(prev => prev.filter(item => item.id !== id));
    }, delay);
  }, []);

  // Helper function to make caption sprites draggable
  const setupCaptionDrag = useCallback((captionSprite, elementId) => {
    if (!viewport) return;
    captionSprite.eventMode = 'static';
    captionSprite.cursor = 'pointer';

    let isDragging = false;
    let dragOffset = { x: 0, y: 0 };

    const onPointerDown = (event) => {
      isDragging = true;
      const worldPosition = viewport.toWorld(event.global.x, event.global.y);
      dragOffset = {
        x: worldPosition.x - captionSprite.x,
        y: worldPosition.y - captionSprite.y,
      };
      viewport.on('pointermove', onPointerMove);
      // Select the parent element so timeline/properties panels reflect it
      const stateElements = useCanvasStore.getState().canvasElements;
      const el = stateElements.find(e => e.id === elementId);
      if (el) selectElement(el);
    };

    const onPointerMove = (event) => {
      if (!isDragging) return;
      const worldPosition = viewport.toWorld(event.global.x, event.global.y);
      let newX = worldPosition.x - dragOffset.x;
      let newY = worldPosition.y - dragOffset.y;

      // Clamp within stage boundaries (anchor is 0.5, so half-width/height offset)
      const halfW = captionSprite.width / 2;
      const halfH = captionSprite.height / 2;
      newX = Math.max(halfW, Math.min(newX, viewport.worldWidth - halfW));
      newY = Math.max(halfH, Math.min(newY, viewport.worldHeight - halfH));

      captionSprite.x = newX;
      captionSprite.y = newY;
    };

    const onPointerUp = () => {
      if (!isDragging) return;
      isDragging = false;
      viewport.off('pointermove', onPointerMove);

      // Save position to store
      const stateElements = useCanvasStore.getState().canvasElements;
      const el = stateElements.find(e => e.id === elementId);
      if (el && el.componentData?.captions) {
        const caps = el.componentData.captions;
        const newStyle = { ...(caps.style || {}), x: captionSprite.x, y: captionSprite.y };
        const newComponentData = {
          ...el.componentData,
          captions: { ...caps, style: newStyle }
        };
        updateElement(elementId, { componentData: newComponentData });
      }
    };

    captionSprite.on('pointerdown', onPointerDown);
    viewport.on('pointerup', onPointerUp);
    viewport.on('pointerupoutside', onPointerUp);

    captionSprite._cleanupDrag = () => {
      captionSprite.off('pointerdown', onPointerDown);
      viewport.off('pointerup', onPointerUp);
      viewport.off('pointerupoutside', onPointerUp);
      viewport.off('pointermove', onPointerMove);
    };
  }, [viewport, updateElement]);

  // Add component to canvas with loading indicator
  const addComponentToCanvas = useCallback(
    async (componentData, position = { x: 0, y: 0 }, layerProperties = null, showLoadingIndicator = true) => {
      if (!viewport) {
        console.warn("Viewport not available");
        return;
      }

      // Generate a unique loading ID
      const loadingId = `loading_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const itemName = componentData.title || componentData.name || componentData.text || 'Media';

      // Determine element type early for the loading indicator
      // Check explicit types first (ai, text) before URL-based pattern matching,
      // otherwise an AI sprite whose URL ends in .png would be detected as 'image'.
      let elementType = 'video'; // default type
      if (componentData.type === 'ai') {
        elementType = 'ai';
      } else if (componentData.type === 'text') {
        elementType = 'text';
      } else if (componentData.type === 'gif' || (componentData.url && componentData.url.match(/\.gif$/i))) {
        elementType = 'gif';
      } else if (componentData.type === 'image' || (componentData.url && componentData.url.match(/\.(jpg|jpeg|png|svg|webp)$/i))) {
        elementType = 'image';
      } else if (componentData.type === 'audio' || (componentData.url && componentData.url.match(/\.(mp3|wav|ogg|m4a)$/i))) {
        elementType = 'audio';
      } else if (componentData.type === 'video' || (componentData.url && componentData.url.match(/\.(mp4|webm|mov|avi|mpeg)$/i))) {
        elementType = 'video';
      }

      // Show loading indicator (skip for template layers to avoid too many toasts)
      if (showLoadingIndicator) {
        addLoadingItem(loadingId, itemName, elementType);
      }

      let sprite = null;

      try {
        // Determine the type of component and create appropriate sprite
        if (elementType === 'gif') {
          sprite = await createGifSprite(componentData, layerProperties?.id || null, position.x, position.y);
        } else if (elementType === 'image') {
          sprite = await createImageSprite(componentData, layerProperties?.id || null, position.x, position.y);
        } else if (elementType === 'audio') {
          sprite = await createAudioSprite(componentData, layerProperties?.id || null, position.x, position.y);
        } else if (elementType === 'text') {
          sprite = await createTextSprite(componentData, layerProperties?.id || null, position.x, position.y);
        } else if (elementType === 'ai') {
          sprite = await createAiSprite(componentData, layerProperties?.id || null, position.x, position.y);
        } else {
          // Default to video sprite for video files or unknown types
          sprite = await createVideoSprite(componentData, layerProperties?.id || null, position.x, position.y, componentData.currSrc || null);
        }

        if (sprite) {
          // Add visual sprite to visual container (gets masked)
          const visualContainer = viewport.visualContainer || viewport;
          visualContainer.addChild(sprite);
          // Don't cache container as texture - it prevents video updates

          // Add interactive area to interactive container (no mask)
          const interactiveContainer = viewport.interactiveContainer || viewport;
          if (sprite.interactiveArea) {
            interactiveContainer.addChild(sprite.interactiveArea);
          }

          let captionSprite = null;

          // Restore Caption Sprite if captions exist on loaded element
          if (componentData?.captions?.phrases?.length > 0) {
            const captionData = componentData.captions;
            const captionStyle = sanitizeCaptionStyle(captionData.style || {});
            const captionFontFamily = captionStyle.fontFamily || 'Arial';
            await ensureFontLoaded(captionFontFamily);

            const needsWordContainer = shouldUseWordContainer(captionStyle);

            if (needsWordContainer) {
              // Word-by-word highlight: Container of individual PIXI.Text per word
              const container = new PIXI.Container();
              container._isWordContainer = true;
              container._highlightColor = captionStyle.highlightColor || null;
              container._baseColor = captionStyle.color || '#ffffff';
              container._captionStyle = captionStyle;
              container._displayMode = captionStyle.displayMode || 'karaoke';
              container._highlightBg = captionStyle.highlightBg || null;
              container._wordAnimation = captionStyle.wordAnimation ?? null;
              container._pastPhraseColor = captionStyle.pastPhraseColor || '#ffffff';
              captionSprite = container;
            } else {
              // Standard PIXI.Text
              const style = new PIXI.TextStyle({
                fontFamily: captionFontFamily,
                fontSize: captionStyle.fontSize || 36,
                fill: captionStyle.color || '#ffffff',
                align: 'center',
                wordWrap: true,
                wordWrapWidth: 1600,
                ...(captionStyle.stroke ? {
                  stroke: captionStyle.stroke,
                  ...(captionStyle.strokeThickness !== undefined ? { strokeThickness: captionStyle.strokeThickness } : {}),
                  ...(captionStyle.lineJoin ? { lineJoin: captionStyle.lineJoin } : {}),
                } : {}),
                // Temporarily disabled drop-shadow styling.
              });

              captionSprite = new PIXI.Text({
                text: captionData.phrases[0].text,
                style
              });
              captionSprite.anchor.set(0.5);
            }

            captionSprite.x = captionStyle.x ?? 960;
            captionSprite.y = captionStyle.y ?? 900;

            // Replaced 'none' with setup helper
            setupCaptionDrag(captionSprite, layerProperties?.id || sprite.elementId);

            viewport.addChild(captionSprite);
          }

          selectElement(null); // Clear previous selection
          addElement({
            id: sprite.elementId,
            type: elementType,
            name: itemName,
            x: sprite.x,
            y: sprite.y,
            width: sprite.width,
            height: sprite.height,
            componentData,
            timeline: layerProperties?.timeline,
            pixiSprite: sprite,
            ...(captionSprite ? { captionSprite } : {}),
            ...(layerProperties ? { layerProperties } : {})
          });

          // console.log(
          //   "Added component to canvas:",
          //   componentData.name + componentData.id
          // );

          // Update loading indicator to success
          if (showLoadingIndicator) {
            updateLoadingItem(loadingId, 'success');
            removeLoadingItem(loadingId, 1500); // Remove after 1.5 seconds
          }
        } else {
          // Sprite creation returned null
          if (showLoadingIndicator) {
            updateLoadingItem(loadingId, 'error', 'Failed to create sprite');
            removeLoadingItem(loadingId, 3000); // Keep error visible longer
          }
        }
      } catch (error) {
        console.error("Error adding component to canvas:", error);

        // Update loading indicator to error
        if (showLoadingIndicator) {
          updateLoadingItem(loadingId, 'error', error.message || 'Failed to add media');
          removeLoadingItem(loadingId, 3000); // Keep error visible longer
        }
      }
    },
    [
      viewport,
      createVideoSprite,
      createImageSprite,
      createAudioSprite,
      createTextSprite,
      createGifSprite,
      createAiSprite,
      addElement,
      selectElement,
      addLoadingItem,
      updateLoadingItem,
      removeLoadingItem,
    ]
  );

  // Replace media for an existing element
  const replaceElementMedia = useCallback(
    async (elementId, newUrl, newMediaType, newName, thumbnailUrl) => {
      if (!viewport) {
        console.warn("Viewport not available for media replacement");
        return;
      }

      // Find the existing element
      const existingElement = canvasElements.find(el => el.id === elementId);
      if (!existingElement) {
        console.warn(`Element with ID ${elementId} not found for replacement`);
        return;
      }

      const itemName = existingElement.name || 'Media';
      const loadingId = `replacing_${Date.now()}`;

      addLoadingItem(loadingId, `Replacing ${itemName}...`, newMediaType);

      try {
        // Prepare new component data based on existing one, but updated with new URL
        const oldComponentData = existingElement.componentData || {};
        // For video/audio, preview & thumbnail should be image poster URLs,
        // NOT the media URL itself (browsers can't render .mp4 as a poster).
        const isPlayableType = newMediaType === 'video' || newMediaType === 'audio';
        const newComponentData = {
          ...oldComponentData,
          url: newUrl,
          preview: isPlayableType
            ? (thumbnailUrl || null)
            : newUrl,
          thumbnail: isPlayableType
            ? (thumbnailUrl || null)
            : (thumbnailUrl || newUrl),
          type: newMediaType,
        };
        // Update name/title properties so the saved project reloads with the correct name
        if (newName) {
          if ('title' in newComponentData) newComponentData.title = newName;
          if ('text' in newComponentData && newMediaType === 'text') newComponentData.text = newName;
          newComponentData.name = newName;
        }
        // Clean out AG Component specifics if we are replacing with raw media
        if (newComponentData.fields) delete newComponentData.fields;
        if (newComponentData.currSrc) delete newComponentData.currSrc;

        // ---- Step 1: Stop playback and deselect BEFORE we touch the old sprite ----
        // Stop playback to prevent the ticker from re-triggering audio between cleanup and new sprite
        if (isPlaying) {
          stopPlaybackLoop();
        }
        // This ensures transform controls release their reference to the old sprite
        selectElement(null);

        // ---- Step 2: Capture old sprite transforms before replacing ----
        const oldSprite = existingElement.pixiSprite;
        let savedAlpha = 1, savedWidth = 0, savedHeight = 0, savedRotation = 0, savedZIndex = 0;
        if (oldSprite && !oldSprite.destroyed && oldSprite.scale !== null) {
          savedAlpha = oldSprite.alpha ?? 1;
          // Save the visual (display) dimensions so the replacement matches
          // the original size regardless of the new texture's native resolution.
          savedWidth = oldSprite.width || 0;
          savedHeight = oldSprite.height || 0;
          savedRotation = oldSprite.rotation ?? 0;
          savedZIndex = oldSprite.zIndex ?? 0;
        }

        // ---- Step 2.5: Cleanup old audio/video BEFORE creating new sprite ----
        if (oldSprite && !oldSprite.destroyed) {
          // Synchronously cleanup audio (stop + remove sound by ID)
          if (oldSprite.soundId) {
            try {
              // Stop the PIXI Sound instance
              if (sound.exists(oldSprite.soundId)) {
                sound.stop(oldSprite.soundId);
                sound.remove(oldSprite.soundId);
              }
              // Also stop via the PIXI Sound instance directly
              if (oldSprite.pixiSound) {
                oldSprite.pixiSound.stop();
              }
            } catch (e) {
              console.warn('Error cleaning up old audio:', e);
            }
            // Pause the underlying HTML Audio element directly
            if (oldSprite.audioElement) {
              try {
                if (oldSprite.audioElement.source) {
                  oldSprite.audioElement.source.stop();
                } else if (oldSprite.audioElement.pause) {
                  oldSprite.audioElement.pause();
                }
              } catch (e) {
                // ignore - element may already be disposed
              }
            }
            oldSprite.soundId = null;
            oldSprite.pixiSound = null;
            oldSprite.audioElement = null;
            // Prevent _cleanupAudio from running its setTimeout later
            oldSprite._cleanupAudio = () => { };
          }
          // Cleanup video
          if (oldSprite.videoElement) {
            oldSprite.videoElement.pause();
            oldSprite.videoElement.removeAttribute('src');
            oldSprite.videoElement.load();
          }
          if (oldSprite._cleanupVideo) {
            oldSprite._cleanupVideo();
          }
          if (oldSprite._cleanupAnimation) {
            oldSprite._cleanupAnimation();
          }
        }

        // ---- Step 3: Create new sprite FIRST (before destroying old) ----
        let newSprite = null;
        if (newMediaType === 'gif') {
          newSprite = await createGifSprite(newComponentData, elementId, existingElement.x, existingElement.y);
        } else if (newMediaType === 'image') {
          newSprite = await createImageSprite(newComponentData, elementId, existingElement.x, existingElement.y);
        } else if (newMediaType === 'audio') {
          newSprite = await createAudioSprite(newComponentData, elementId, existingElement.x, existingElement.y);
        } else {
          newSprite = await createVideoSprite(newComponentData, elementId, existingElement.x, existingElement.y, null);
        }

        if (!newSprite) {
          updateLoadingItem(loadingId, 'error', 'Failed to create replacement sprite');
          removeLoadingItem(loadingId, 3000);
          return;
        }

        // ---- Step 4: Scale new sprite to match the old sprite's visual size ----
        // The new texture may have different native dimensions, so we compute
        // the scale factors needed to reproduce the exact same on-screen size.
        newSprite.alpha = savedAlpha;
        newSprite.rotation = savedRotation;
        newSprite.zIndex = savedZIndex;

        if (savedWidth > 0 && savedHeight > 0) {
          // Get the new sprite's native (unscaled) texture dimensions.
          // Reset scale to 1 first so .width/.height reflect the texture size.
          newSprite.scale.set(1, 1);
          const nativeW = newSprite.width || 1;
          const nativeH = newSprite.height || 1;
          const newScaleX = savedWidth / nativeW;
          const newScaleY = savedHeight / nativeH;
          newSprite.scale.set(newScaleX, newScaleY);
        }
        // else: no saved dimensions (e.g. audio) — keep the sprite's default scale

        if (newSprite.interactiveArea) {
          newSprite.interactiveArea.alpha = savedAlpha;
          newSprite.interactiveArea.scale.set(newSprite.scale.x, newSprite.scale.y);
          newSprite.interactiveArea.rotation = savedRotation;
          newSprite.interactiveArea.zIndex = savedZIndex;
        }

        // ---- Step 5: Swap containers (remove old, add new) ----
        const visualContainer = viewport.visualContainer || viewport;
        const interactiveContainer = viewport.interactiveContainer || viewport;

        // Remove old sprite from containers (but don't destroy yet)
        if (oldSprite) {
          if (oldSprite.interactiveArea && oldSprite.interactiveArea.parent) {
            oldSprite.interactiveArea.parent.removeChild(oldSprite.interactiveArea);
          }
          if (oldSprite.parent) {
            oldSprite.parent.removeChild(oldSprite);
          }
        }

        // Add new sprite to containers
        visualContainer.addChild(newSprite);
        if (newSprite.interactiveArea) {
          interactiveContainer.addChild(newSprite.interactiveArea);
        }
        visualContainer.sortDirty = true;
        interactiveContainer.sortDirty = true;

        let captionSprite = null;

        // Restore Caption Sprite if captions exist on loaded element
        if (newComponentData?.captions?.phrases?.length > 0) {
          const captionData = newComponentData.captions;
          const captionStyle = sanitizeCaptionStyle(captionData.style || {});
          const captionFontFamily = captionStyle.fontFamily || 'Arial';
          await ensureFontLoaded(captionFontFamily);

          const needsWordContainer = shouldUseWordContainer(captionStyle);

          if (needsWordContainer) {
            const container = new PIXI.Container();
            container._isWordContainer = true;
            container._highlightColor = captionStyle.highlightColor || null;
            container._baseColor = captionStyle.color || '#ffffff';
            container._captionStyle = captionStyle;
            container._displayMode = captionStyle.displayMode || 'karaoke';
            container._highlightBg = captionStyle.highlightBg || null;
            container._wordAnimation = captionStyle.wordAnimation ?? null;
            container._pastPhraseColor = captionStyle.pastPhraseColor || '#ffffff';
            captionSprite = container;
          } else {
            const style = new PIXI.TextStyle({
              fontFamily: captionFontFamily,
              fontSize: captionStyle.fontSize || 36,
              fill: captionStyle.color || '#ffffff',
              align: 'center',
              wordWrap: true,
              wordWrapWidth: 1600,
              ...(captionStyle.stroke ? {
                stroke: captionStyle.stroke,
                ...(captionStyle.strokeThickness !== undefined ? { strokeThickness: captionStyle.strokeThickness } : {}),
                ...(captionStyle.lineJoin ? { lineJoin: captionStyle.lineJoin } : {}),
              } : {}),
              // Temporarily disabled drop-shadow styling.
            });

            captionSprite = new PIXI.Text({
              text: captionData.phrases[0].text,
              style
            });
            captionSprite.anchor.set(0.5);
          }

          captionSprite.x = captionStyle.x ?? 960;
          captionSprite.y = captionStyle.y ?? 900;

          setupCaptionDrag(captionSprite, elementId);

          viewport.addChild(captionSprite);
        }

        // ---- Step 6: Update store with new sprite ----
        // Persist scaleX/scaleY so autosave writes them to the project
        // and the correct size is restored on reload.
        updateElement(elementId, {
          type: newMediaType,
          name: newName || existingElement.name,
          componentData: newComponentData,
          pixiSprite: newSprite,
          ...(captionSprite ? { captionSprite } : {}),
          width: newSprite.width,
          height: newSprite.height,
          scaleX: newSprite.scale.x,
          scaleY: newSprite.scale.y,
          rotation: newSprite.rotation,
          alpha: newSprite.alpha,
        });

        // ---- Step 7: Destroy old sprite AFTER store is updated ----
        if (oldSprite && !oldSprite.destroyed) {
          oldSprite.destroy({ children: true });
        }

        // Destroy old caption sprite if it existed
        if (existingElement.captionSprite && !existingElement.captionSprite.destroyed) {
          if (existingElement.captionSprite.parent) {
            existingElement.captionSprite.parent.removeChild(existingElement.captionSprite)
          }
          existingElement.captionSprite.destroy();
        }

        updateLoadingItem(loadingId, 'success');
        removeLoadingItem(loadingId, 1500);
      } catch (error) {
        console.error("Error replacing media:", error);
        updateLoadingItem(loadingId, 'error', error.message || 'Failed to replace media');
        removeLoadingItem(loadingId, 3000);
      }
    },
    [
      viewport,
      canvasElements,
      createVideoSprite,
      createImageSprite,
      createAudioSprite,
      createGifSprite,
      updateElement,
      addLoadingItem,
      updateLoadingItem,
      removeLoadingItem,
      selectElement
    ]
  );

  useEffect(() => {
    if (addItemRequest && viewport) {
      // console.log("Handling add item request from store:", addItemRequest.data);

      // Add the component to the center of the current viewport view
      const centerPosition = viewport.center;

      // Extract layer properties if they were explicitly bundled inside the request
      const itemData = addItemRequest.data;
      const layerProps = itemData.layerProperties || null;

      addComponentToCanvas(itemData, centerPosition, layerProps);

      // Clear the request so it doesn't fire again
      clearAddItemRequest();
    }
  }, [addItemRequest, addComponentToCanvas, clearAddItemRequest, viewport]);

  // Handle drop of template with multiple layers
  const handleTemplateDrop = useCallback(
    async (template, worldPosition, skipSizeCheck = false) => {
      if (!template.layers || template.layers.length === 0) {
        console.warn("Template has no layers to add");
        return;
      }

      // Check if template canvas size matches current canvas size
      if (!skipSizeCheck && template.canvas) {
        const templateWidth = template.canvas.width;
        const templateHeight = template.canvas.height;
        const currentWidth = canvasSize.width;
        const currentHeight = canvasSize.height;

        if (templateWidth !== currentWidth || templateHeight !== currentHeight) {
          // Canvas size mismatch - store template and show modal
          setPendingTemplate(template);
          setPendingWorldPosition(worldPosition);
          setShowSizeMismatchModal(true);
          return; // Don't proceed with adding template yet
        }
      }

      console.log(`📦 Adding template with ${template.layers.length} layers...`);

      // Start loading progress for template
      const totalLayers = template.layers.length;
      setTemplateLoadingProgress({
        isOpen: true,
        progress: 0,
        currentAsset: 'Preparing template...',
        totalAssets: totalLayers,
        loadedAssets: 0,
        stage: 'initializing'
      });

      try {
        // Calculate the highest current zIndex to offset template layers
        const maxCurrentZIndex = canvasElements.length > 0
          ? Math.max(...canvasElements.map((el) => el.zIndex || 0))
          : -1;

        // Sort template layers by their original zIndex to maintain order
        const sortedLayers = [...template.layers].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

        // Update progress to loading stage
        setTemplateLoadingProgress(prev => ({
          ...prev,
          progress: 10,
          currentAsset: 'Loading template layers...',
          stage: 'loading-assets'
        }));

        // Track loaded layers
        let loadedCount = 0;
        const baseProgress = 10;
        const progressPerLayer = (90 - baseProgress) / totalLayers;

        // Add each layer to canvas without their original IDs
        // addComponentToCanvas will automatically create new IDs
        for (let i = 0; i < sortedLayers.length; i++) {
          const layer = sortedLayers[i];

          // Update progress before loading each layer
          const layerName = layer.name || layer.componentData?.name || `Layer ${i + 1}`;
          setTemplateLoadingProgress(prev => ({
            ...prev,
            currentAsset: layerName,
            loadedAssets: loadedCount,
            progress: baseProgress + (loadedCount * progressPerLayer)
          }));

          try {
            // Extract position from layer data (templates already have correct positions)
            const position = {
              x: layer.x || 960,
              y: layer.y || 540
            };

            // Extract layer properties without the ID (so new IDs are generated)
            // But include the zIndex offset to maintain relative order
            const { id, ...layerPropertiesWithoutId } = layer;

            // Calculate new zIndex: start from maxCurrentZIndex + 1, then add layer's relative position
            const newZIndex = maxCurrentZIndex + 1 + i;

            const layerPropertiesWithNewZIndex = {
              ...layerPropertiesWithoutId,
              zIndex: newZIndex
            };

            // Add component to canvas (creates new sprite with new ID)
            // Pass false for showLoadingIndicator since templates have their own loading modal
            await addComponentToCanvas(layer.componentData, position, layerPropertiesWithNewZIndex, false);

            console.log(`✅ Added layer: ${layer.name} with zIndex ${newZIndex}`);

            // Update loaded count and progress after successful load
            loadedCount++;
            setTemplateLoadingProgress(prev => ({
              ...prev,
              loadedAssets: loadedCount,
              progress: baseProgress + (loadedCount * progressPerLayer)
            }));

          } catch (error) {
            console.error(`❌ Error adding layer ${layer.name}:`, error);
            // Continue loading other layers even if one fails
            loadedCount++;
          }
        }

        // Finalize loading
        setTemplateLoadingProgress(prev => ({
          ...prev,
          progress: 95,
          currentAsset: 'Finalizing template...',
          stage: 'finalizing'
        }));

        console.log("🎉 Template added successfully!");

        // Complete loading
        setTimeout(() => {
          setTemplateLoadingProgress(prev => ({
            ...prev,
            progress: 100,
            currentAsset: 'Complete!',
            isOpen: false
          }));
        }, 500);

      } catch (error) {
        console.error("❌ Error adding template:", error);
        // Close loading progress on error
        setTemplateLoadingProgress(prev => ({
          ...prev,
          isOpen: false
        }));
      }
    },
    [addComponentToCanvas, canvasElements, canvasSize, setCanvasSize]
  );

  // Handler for when user chooses to change canvas size and add template
  const handleChangeCanvasSizeAndAdd = useCallback(() => {
    if (pendingTemplate && pendingTemplate.canvas) {
      // Change canvas size to match template (including name)
      setCanvasSize({
        width: pendingTemplate.canvas.width,
        height: pendingTemplate.canvas.height,
        name: pendingTemplate.canvas.name || `${pendingTemplate.canvas.width} × ${pendingTemplate.canvas.height}`
      });

      // Add template with size check skipped
      handleTemplateDrop(pendingTemplate, pendingWorldPosition, true);

      // Clear pending state and close modal
      setPendingTemplate(null);
      setPendingWorldPosition(null);
      setShowSizeMismatchModal(false);
    }
  }, [pendingTemplate, pendingWorldPosition, setCanvasSize, handleTemplateDrop]);

  // Handler for when user chooses to continue without changing canvas size
  const handleContinueWithoutChange = useCallback(() => {
    if (pendingTemplate) {
      // Add template without changing canvas size
      handleTemplateDrop(pendingTemplate, pendingWorldPosition, true);

      // Clear pending state and close modal
      setPendingTemplate(null);
      setPendingWorldPosition(null);
      setShowSizeMismatchModal(false);
    }
  }, [pendingTemplate, pendingWorldPosition, handleTemplateDrop]);

  // Handler for when user cancels the operation
  const handleCancelTemplateDrop = useCallback(() => {
    // Clear pending state and close modal
    setPendingTemplate(null);
    setPendingWorldPosition(null);
    setShowSizeMismatchModal(false);
  }, []);

  // Handle drop from library
  const handleCanvasDrop = useCallback(
    async (event) => {
      event.preventDefault();

      try {
        const droppedData = JSON.parse(
          event.dataTransfer.getData("application/json")
        );

        if (!viewport) {
          console.warn("Viewport not available for drop");
          return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        const localX = event.clientX - rect.left;
        const localY = event.clientY - rect.top;
        const worldPosition = viewport.toWorld(localX, localY);

        // Check if this is a template (has layers array) or a single component
        if (droppedData.layers && Array.isArray(droppedData.layers)) {
          // This is a template - add all its layers
          await handleTemplateDrop(droppedData, worldPosition);
        } else {
          // This is a single component - add it normally
          await addComponentToCanvas(droppedData, worldPosition);
        }
      } catch (error) {
        console.error("Error handling canvas drop:", error);
      }
    },
    [viewport, addComponentToCanvas, handleTemplateDrop]
  );

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  }, []);

  return {
    addComponentToCanvas,
    handleCanvasDrop,
    handleDragOver,
    createVideoSprite,
    createImageSprite,
    createAudioSprite,
    createTextSprite,
    createGifSprite,
    replaceElementMedia,
    showTransformControls,
    hideTransformControls,
    // Canvas size mismatch modal state and handlers
    showSizeMismatchModal,
    pendingTemplate,
    handleChangeCanvasSizeAndAdd,
    handleContinueWithoutChange,
    handleCancelTemplateDrop,
    // Template loading progress
    templateLoadingProgress,
    // Sprite loading indicators
    spriteLoadingItems,
    // Component update indicators (for re-render toasts)
    componentUpdateItems,
    // Element reloading check function
    isElementReloading,
  };
};
